import { useEffect, useMemo, useRef } from 'react';
import isEqual from 'lodash/isEqual';
import { useQueryParams } from '../query-params/index.tsx';

type Props<T> = {
	state: T;
	getStateFromQueryParams: (arg1: { [key: string]: string }) => T;
	getQueryParamsFromState: (state: T) => {
		[key: string]: string | null | undefined;
	};
	queryParamsToWatch: string[];
	onStateChange: (newState: T) => void;
	onReset: () => void;
};

/**
 *
 * A hook to sync some form of React state with the browser URL query params.
 *
 * @param state - the internal state object you are using e.g. react-sweet-state
 * @param onStateChange - the function to apply state changes e.g. setState
 * @param onReset - the function to reset the state and is called on component unmount
 * @param queryParamsToWatch - an array of strings identifying the query params to sync with
 * @param getStateFromQueryParams - transforms a query params object into a state object
 * @param getQueryParamsFromState - transforms a state object into a query params object
 */
export function useQueryParamsSync<T>({
	state,
	onStateChange,
	onReset,
	queryParamsToWatch,
	getStateFromQueryParams,
	getQueryParamsFromState,
}: Props<T>) {
	const [queryParams, setQueryParams] = useQueryParams(...queryParamsToWatch);

	const stateFromQuery = useMemo(
		() => getStateFromQueryParams(queryParams),
		[queryParams, getStateFromQueryParams],
	);
	const stateRef = useRef(stateFromQuery);

	const queryFromState = useMemo(
		() => getQueryParamsFromState(state),
		[state, getQueryParamsFromState],
	);
	const queryRef = useRef(queryFromState);

	// *** Set the initial state ***
	// on initial load Query string is the source of truth
	// This will run on the server render and the initial client render
	const initialLoad = useRef(true);
	if (initialLoad.current && Object.values(queryParams).some((value) => value !== undefined)) {
		onStateChange(stateFromQuery);
	}
	initialLoad.current = false;

	// Effect to reset filter state when navigating away from page
	useEffect(() => () => onReset(), [onReset]);

	// Effect to sync State to match updated Query
	useEffect(() => {
		if (!isEqual(stateFromQuery, stateRef.current)) {
			if (!isEqual(stateFromQuery, state)) {
				onStateChange(stateFromQuery);
			}
			stateRef.current = stateFromQuery;
		}
	}, [onStateChange, stateFromQuery, state]);

	// Effect to sync Query to match updated State
	useEffect(() => {
		if (!isEqual(queryFromState, queryRef.current)) {
			if (!isEqual(queryFromState, queryParams)) {
				setQueryParams(queryFromState, { sort: false });
			}
			queryRef.current = queryFromState;
		}
	}, [queryFromState, queryParams, setQueryParams]);
}
